/**
 * User Templates Process Handlers
 * Handlers for saving, deleting, and renaming user-owned purpose templates via Cloud Functions.
 */

import { httpsCallable } from 'firebase/functions'
import { functions } from '../../libs/Firebase'
import { handleRequest } from './config'
import type { ApiResponse } from '../types'
import type { Activity } from '../../features/facilitator/facilitatorTrainingSprintCreation/types'
import type { PurposeTemplateActivity } from '../../../../pure-js/types/PurposeTemplate'
import { shouldShowLoopConfiguration } from '../../features/facilitator/facilitatorTrainingSprintCreation/utils/activityConfiguration'

function toTemplateActivities(activities: Activity[]): PurposeTemplateActivity[] {
  return activities.map((a) => ({
    type: a.type,
    ...(a.numberOfLoops !== undefined && { numberOfLoops: a.numberOfLoops }),
    ...(a.customSlotLength !== undefined && { customSlotLength: a.customSlotLength }),
    // Persist custom length only for non-game activities; game length is slot/loop derived.
    ...(!shouldShowLoopConfiguration(a.type) && a.duration !== undefined && { duration: a.duration })
  }))
}

export async function saveUserTemplate(name: string, activities: Activity[]): Promise<ApiResponse<void>> {
  const id = crypto.randomUUID()
  const createdAt = new Date().toISOString()
  return handleRequest(
    () => Promise.resolve({ data: undefined, success: true } as ApiResponse<void>),
    async () => {
      const fn = httpsCallable<
        { id: string; name: string; activities: PurposeTemplateActivity[]; createdAt: string },
        { success: boolean }
      >(functions, 'saveUserPurposeTemplate')
      const result = await fn({ id, name, activities: toTemplateActivities(activities), createdAt })
      return { data: undefined, success: result.data?.success ?? true }
    }
  )
}

export async function deleteUserTemplate(templateId: string): Promise<ApiResponse<void>> {
  return handleRequest(
    () => Promise.resolve({ data: undefined, success: true } as ApiResponse<void>),
    async () => {
      const fn = httpsCallable<{ templateId: string }, { success: boolean }>(functions, 'deleteUserPurposeTemplate')
      const result = await fn({ templateId })
      return { data: undefined, success: result.data?.success ?? true }
    }
  )
}

export async function renameUserTemplate(templateId: string, name: string): Promise<ApiResponse<void>> {
  return handleRequest(
    () => Promise.resolve({ data: undefined, success: true } as ApiResponse<void>),
    async () => {
      const fn = httpsCallable<{ templateId: string; name: string }, { success: boolean }>(
        functions,
        'renameUserPurposeTemplate'
      )
      const result = await fn({ templateId, name })
      return { data: undefined, success: result.data?.success ?? true }
    }
  )
}
